import { LiveAnnouncer } from '@angular/cdk/a11y'
import {
  Component,
  Injector,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import {
  MatChipEditedEvent,
  MatChipInputEvent,
  MatChipsModule,
} from '@angular/material/chips'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { ENTER, SEMICOLON } from '@angular/cdk/keycodes'
import { SpinnerService } from '../../../services/spinner.service'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { ApiError } from '../../../types/apiError'
import {
  IngredientCreate,
  IngredientEdit,
  IngredientListElement,
} from '../../../types/ingredients'
import { IngredientService } from '../../../services/ingredient.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ActivatedRoute, Router } from '@angular/router'
import { RouteService } from '../../../services/route.service'
import { ROUTES } from '../../../constants/routes'
import { TranslationService } from '../../../services/translation.service'

@Component({
  selector: 'app-ingredient-create',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
    MatChipsModule,
  ],
  templateUrl: './ingredient-create.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ingredient-create.component.scss',
})
export class IngredientCreateComponent implements OnInit {
  injector = inject(Injector)
  formBuilder = inject(FormBuilder)
  spinnerService = inject(SpinnerService)
  dialog = inject(Dialog)
  errorService = inject(ErrorService)
  dialogService = inject(DialogService)
  ingredientService = inject(IngredientService)
  route = inject(ActivatedRoute)
  routeService = inject(RouteService)
  router = inject(Router)
  translationService = inject(TranslationService)

  ingredientForm = this.formBuilder.group({
    name: ['', [Validators.required]],
  })

  isEditMode: boolean = false
  ingredientId: string | null = null
  ingredientForEdit: IngredientListElement | undefined

  title = this.translationService.getCreateIngredientText()

  readonly addOnBlur = true
  readonly separatorKeysCodes = [ENTER, SEMICOLON] as const
  readonly otherNames = signal<Array<string>>([])
  readonly casNumbers = signal<Array<string>>([])
  readonly announcer = inject(LiveAnnouncer)

  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      const ingredientId = params.get('id')
      this.isEditMode = !!ingredientId

      const currentRoute = this.routeService.getUserRoutes(
        ROUTES.VITE_MARKETPLACE.title,
        ROUTES.INGREDIENTS.path,
      )

      this.routeService.setRoute(currentRoute)

      if (this.isEditMode) {
        this.title = this.translationService.getEditIngredientText()
        await this.setIngredient(ingredientId!)
      }
    })
  }

  /**
   * Sets the ingredient data for Edit Mode
   * @param {string} ingredientId Ingredient id from route params
   */
  async setIngredient(ingredientId: string) {
    try {
      this.ingredientId = ingredientId!
      this.ingredientForEdit = await firstValueFrom(
        this.ingredientService.getById(this.ingredientId),
      )

      this.setFields()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Sets UI fields for Edit Mode
   */
  setFields() {
    if (this.ingredientForEdit) {
      this.ingredientForm.patchValue({
        name: this.ingredientForEdit.name,
      })

      // reset existing signals
      this.otherNames.set([])
      this.casNumbers.set([])

      // update the signals used to track the elements in the mat-chip-lists
      this.ingredientForEdit.otherNames.forEach((otherName) => {
        this.otherNames.update((otherNames) => [...otherNames, otherName])
      })
      this.ingredientForEdit.casNumbers.forEach((casNumber) => {
        this.casNumbers.update((casNumbers) => [...casNumbers, casNumber])
      })
    }
  }

  /**
   * Handles add alias chip event
   * @param {MatChipInputEvent} event Event
   */
  addOtherName(event: MatChipInputEvent): void {
    const value = (event.value || '').trim()
    const alreadyExists = this.otherNames().some(
      (name) => name.toLowerCase() === value.toLowerCase(),
    )

    if (alreadyExists) {
      return
    }

    // Add our otherName
    if (value) {
      this.otherNames.update((otherNames) => [...otherNames, value])
    }

    // Clear the input value
    event.chipInput!.clear()
  }

  /**
   * Handles remove alias chip event
   * @param {string} otherName Alias
   */
  removeOtherName(otherName: string): void {
    this.otherNames.update((otherNames) => {
      const index = otherNames.indexOf(otherName)
      if (index < 0) {
        return otherNames
      }

      otherNames.splice(index, 1)
      this.announcer.announce(`Removed ${otherName}`)
      return [...otherNames]
    })
  }

  /**
   * Handles edit alias chip event
   * @param {string} otherName Alias
   * @param {MatChipEditedEvent} event Event
   */
  editOtherName(otherName: string, event: MatChipEditedEvent) {
    const value = event.value.trim()

    // Remove otherName if it no longer has a name
    if (!value) {
      this.removeOtherName(otherName)
      return
    }

    // Edit existing otherName
    this.otherNames.update((otherNames) => {
      const index = otherNames.indexOf(otherName)
      if (index >= 0) {
        otherNames[index] = value
        return [...otherNames]
      }
      return otherNames
    })
  }

  /**
   * Handles add cas number chip event
   * @param {MatChipInputEvent} event Event
   */
  addCasNumber(event: MatChipInputEvent): void {
    const value = (event.value || '').trim()
    const alreadyExists = this.casNumbers().some(
      (name) => name.toLowerCase() === value.toLowerCase(),
    )

    if (alreadyExists) {
      return
    }

    // Add our otherName
    if (value) {
      this.casNumbers.update((casNumbers) => [...casNumbers, value])
    }

    // Clear the input value
    event.chipInput!.clear()
  }

  /**
   * Handles remove cas number
   * @param {string} casNumber Cas number
   */
  removeCasNumber(casNumber: string): void {
    this.casNumbers.update((casNumbers) => {
      const index = casNumbers.indexOf(casNumber)
      if (index < 0) {
        return casNumbers
      }

      casNumbers.splice(index, 1)
      this.announcer.announce(`Removed ${casNumber}`)
      return [...casNumbers]
    })
  }

  /**
   * Handles edit cas number chip event
   * @param {string} casNumber Cas number
   * @param {MatChipEditedEvent} event Event
   */
  editCasNumber(casNumber: string, event: MatChipEditedEvent) {
    const value = event.value.trim()

    // Remove casNumber if it no longer valid
    if (!value) {
      this.removeCasNumber(casNumber)
      return
    }

    // Edit existing casNumber
    this.otherNames.update((casNumbers) => {
      const index = casNumbers.indexOf(casNumber)
      if (index >= 0) {
        casNumbers[index] = value
        return [...casNumbers]
      }
      return casNumbers
    })
  }

  /**
   * Submits form data to api
   */
  async onSubmit() {
    if (!this.ingredientForm.valid) {
      return
    }

    try {
      this.spinnerService.startSpinner()
      const ingredient: IngredientCreate = {
        name: this.ingredientForm.controls.name.value!,
        otherNames: this.otherNames(),
        casNumbers: this.casNumbers(),
      }

      if (!this.isEditMode) {
        await firstValueFrom(this.ingredientService.create(ingredient))
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getIngredientsText(),
          this.translationService.getIngredientCreatedSuccessfullyText(),
        )

        // reset fields upon submission success
        this.ingredientForm.reset({
          name: '',
        })
        this.ingredientForm.controls.name.setErrors(null)
        this.otherNames.set([])
        this.casNumbers.set([])
      } else {
        const updates: IngredientEdit = {
          otherNames: ingredient.otherNames,
          casNumbers: ingredient.casNumbers,
        }
        await firstValueFrom(
          this.ingredientService.edit(this.ingredientId!, updates),
        )
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getIngredientsText(),
          this.translationService.getIngredientUpdatedSuccessfullyText(),
          undefined,
          this.router,
          '/ingredients',
        )
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form data
   */
  onReset() {
    if (!this.isEditMode) {
      this.ingredientForm.reset()
      this.otherNames.set([])
      this.casNumbers.set([])
    } else {
      this.setFields()
    }
  }
}
